import React, { useState, useRef, useEffect } from 'react';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { useChat } from '@/contexts/ChatContext';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useBusinessOwner } from '@/hooks/useBusinessOwner';
import { usePresence } from '@/hooks/usePresence';
import { CHAT_ROLE } from '@/utils/ChatUtils';
import { useImageUpload } from '@/hooks/useImageUpload';
import ChatHeader from './chat/ChatHeader';
import ChatMessages from './chat/ChatMessages';
import ChatInput from './chat/ChatInput';
import ChatPresenceStatus from './chat/ChatPresenceStatus';

interface ChatSidebarProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  counterpartId: string;
  counterpartName: string;
  counterpartLogo?: string | null;
  userRole: string;
}

const ChatSidebar: React.FC<ChatSidebarProps> = ({
  open,
  onOpenChange,
  counterpartId,
  counterpartName,
  counterpartLogo,
  userRole
}) => {
  const { user } = useAuth();
  const { 
    getCurrentMessages,
    loadingMessages, 
    sendMessage, 
    sendMessageWithImages,
    startConversation,
    checkExistingConversation,
    loadConversation,
    currentConversation,
    isUserTyping,
    setUserTyping 
  } = useChat();

  const { trackUserPresence, untrackUserPresence, isUserOnline } = usePresence();
  
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [showImageUpload, setShowImageUpload] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const businessOwnerQuery = useBusinessOwner(
    userRole === CHAT_ROLE.COMMUNITY_MEMBER ? counterpartId : undefined
  );
  
  const userIdToTrack = React.useMemo(() => {
    if (userRole === CHAT_ROLE.COMMUNITY_MEMBER) {
      return businessOwnerQuery.data || null;
    } else {
      return counterpartId;
    }
  }, [userRole, counterpartId, businessOwnerQuery.data]);

  // Image upload hook - now uses deferred upload
  const {
    pendingImages,
    addImages,
    removeImage,
    clearImages,
    canSend: canSendImages,
    hasImages
  } = useImageUpload();

  const messages = getCurrentMessages();
  
  useEffect(() => {
    console.log(`[ChatSidebar] Presence tracking effect triggered - open: ${open}, userIdToTrack: ${userIdToTrack || 'null'}`);
    
    if (open && userIdToTrack) {
      console.log(`[ChatSidebar] Setting up presence tracking for user ${userIdToTrack}`);
      const timeoutId = setTimeout(() => {
        console.log(`[ChatSidebar] Timeout completed, tracking presence for user ${userIdToTrack}`);
        trackUserPresence(userIdToTrack);
      }, 500);
      return () => {
        console.log(`[ChatSidebar] Cleaning up presence tracking for user ${userIdToTrack}`);
        clearTimeout(timeoutId);
        untrackUserPresence(userIdToTrack);
      };
    }
  }, [open, userIdToTrack, trackUserPresence, untrackUserPresence]);

  useEffect(() => {
    if (initializing) {
      const timeoutId = setTimeout(() => {
        setInitializing(false);
      }, 5000);
      return () => clearTimeout(timeoutId);
    }
  }, [initializing]);

  useEffect(() => {
    if (!open) {
      setInitializing(false);
      setShowImageUpload(false);
      clearImages();
    }
  }, [open, clearImages]);

  useEffect(() => {
    if (open && counterpartId) {
      handleCheckExistingChat();
    }
  }, [open, counterpartId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (open) {
      document.body.classList.add('sheet-open');
    } else {
      document.body.classList.remove('sheet-open');
    }
    
    return () => {
      document.body.classList.remove('sheet-open');
    };
  }, [open]);

  const handleCheckExistingChat = async () => {
    if (!user) {
      return;
    }
    
    console.log(`[ChatSidebar] Starting to check for existing conversation with counterpartId: ${counterpartId}`);
    setInitializing(true);
    
    let retryCount = 0;
    const maxRetries = 2;
    
    const attemptLoadConversation = async () => {
      try {
        console.log('[ChatSidebar] Checking for existing conversation');
        const conversation = await checkExistingConversation(counterpartId, userRole);
        
        if (conversation) {
          console.log(`[ChatSidebar] Found existing conversation with ID: ${conversation.id}, loading it`);
          try {
            await loadConversation(conversation.id);
            console.log('[ChatSidebar] Conversation loaded successfully');
          } catch (loadError) {
            console.error('[ChatSidebar] Error loading conversation:', loadError);
            
            if (retryCount < maxRetries) {
              retryCount++;
              console.log(`[ChatSidebar] Retrying load conversation (${retryCount}/${maxRetries})`);
              return attemptLoadConversation();
            }
          }
        } else {
          console.log('[ChatSidebar] No existing conversation found, ready to start a new one');
        }
      } catch (error) {
        console.error('[ChatSidebar] Error checking existing chat:', error);
        
        if (retryCount < maxRetries) {
          retryCount++;
          console.log(`[ChatSidebar] Retrying check existing conversation (${retryCount}/${maxRetries})`);
          return attemptLoadConversation();
        }
      }
    };
    
    try {
      await attemptLoadConversation();
    } finally {
      console.log('[ChatSidebar] Finished checking for existing conversation, setting initializing to false');
      setInitializing(false);
    }
  };

  const handleSendMessage = async () => {
    const hasText = messageText.trim();
    const hasImages = pendingImages.length > 0;
    
    if (!hasText && !hasImages) {
      return;
    }
    
    console.log('[ChatSidebar] Sending message with text:', hasText, 'and images:', hasImages);
    setSending(true);
    
    try {
      let conversationToUse = currentConversation;
      
      if (!conversationToUse) {
        console.log('[ChatSidebar] No current conversation, starting a new one');
        conversationToUse = await startConversation(counterpartId, userRole);
        console.log('[ChatSidebar] New conversation started successfully');
      }
      
      if (hasImages) {
        // Send message with images (will be uploaded during send)
        console.log('[ChatSidebar] Sending message with images');
        await sendMessageWithImages(
          messageText.trim() || '', 
          pendingImages,
          conversationToUse
        );
        console.log('[ChatSidebar] Message with images sent successfully');
      } else {
        // Send text-only message
        console.log('[ChatSidebar] Sending text-only message');
        await sendMessage(messageText.trim(), conversationToUse);
        console.log('[ChatSidebar] Text message sent successfully');
      }
      
      // Clear input and images
      setMessageText('');
      clearImages();
      setShowImageUpload(false);
      
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    } catch (error) {
      console.error('[ChatSidebar] Error sending message:', error);
    } finally {
      console.log('[ChatSidebar] Finished sending message, setting sending to false');
      setSending(false);
    }
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessageText(e.target.value);
    
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 150)}px`;
    }
    
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    if (currentConversation) {
      console.log('[ChatSidebar] Setting typing status to true for conversation:', currentConversation.id);
      setUserTyping(currentConversation.id, true);
      
      typingTimeoutRef.current = setTimeout(() => {
        console.log('[ChatSidebar] Setting typing status to false for conversation:', currentConversation.id);
        setUserTyping(currentConversation.id, false);
      }, 3000);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleTextareaBlur = () => {
    setTimeout(() => {
      if (window.visualViewport) {
        window.scrollTo(0, 0);
      }
    }, 100);
  };

  const handleToggleImageUpload = () => {
    setShowImageUpload(!showImageUpload);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const renderStatus = () => (
    <ChatPresenceStatus 
      userIdToTrack={userIdToTrack}
      isUserOnline={isUserOnline}
    />
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className={cn(
        "sm:max-w-md p-0 flex flex-col h-full",
        "mobile-sheet-content"
      )}>
        <ChatHeader
          counterpartName={counterpartName}
          counterpartLogo={counterpartLogo}
          getInitials={getInitials}
          renderStatus={renderStatus}
        />
        
        <ChatMessages
          messages={messages}
          loadingMessages={loadingMessages}
          initializing={initializing}
          currentConversation={currentConversation}
          userId={user?.id}
          isUserTyping={isUserTyping}
          messagesEndRef={messagesEndRef}
        />
        
        <ChatInput
          messageText={messageText}
          sending={sending}
          loadingMessages={loadingMessages}
          initializing={initializing}
          textareaRef={textareaRef}
          onTextareaChange={handleTextareaChange}
          onKeyDown={handleKeyDown}
          onTextareaBlur={handleTextareaBlur}
          onSendMessage={handleSendMessage}
          pendingImages={pendingImages}
          onAddImages={addImages}
          onRemoveImage={removeImage}
          showImageUpload={showImageUpload}
          onToggleImageUpload={handleToggleImageUpload}
        />
      </SheetContent>
    </Sheet>
  );
};

export default ChatSidebar;
